import { PropType, reactive } from 'vue';
import BaseSetupService from '@/setup/BaseSetupService';
import { DOCUMENT_TYPE } from '@/typings/enums/DOCUMENT_TYPE';
import { getApi } from '@/utils/ApiTool';
import { qsTo64 } from '@/utils/tools';
import { isArray } from 'lodash-es';
import { DOCUMENT_STATUS } from '@/typings/enums/DOCUMENT_STATUS';
import { itemHas } from '@orion.ui/orion';
import useContractService, { ContractService } from '@/services/contract/ContractService';
import useProvisionDocumentService, { ProvisionDocumentService } from '@/services/provision-document/ProvisionDocumentService';
import { DOCUMENT_STATE } from '@/typings/enums/DOCUMENT_STATE';

type Props = SetupProps<typeof DashboardNewDocOrSignCardSetupService.props>
type FilterType = {
	documentState?: DOCUMENT_STATE,
	documentStatus?: DOCUMENT_STATUS,
}

type DocumentType = {
	type: string,
	api: ReturnType<typeof getApi> | undefined,
	ids: number[],
	count: number,
	service?: ContractService | ProvisionDocumentService,
	documentType: DOCUMENT_TYPE,
	readable: (isAgency?: boolean) => string
}

export default class DashboardNewDocOrSignCardSetupService extends BaseSetupService<Props> {
	static readonly props = {
		late: Boolean,
		type: {
			type: String as PropType<'notSigned' | 'notViewed'>,
			default: undefined,
		},
		color: {
			type: String as PropType<Orion.Color>,
			default: undefined,
		},
	};

	private readonly state = reactive({
		documentType: undefined as Undef<DOCUMENT_TYPE>,
		dataLoaded: false,
	});

	readonly newDocs = [
		{
			type: 'Certificate',
			api: this.self.isTempWorker() ? getApi(this.self.userType, DOCUMENT_TYPE.Certificate) : undefined,
			ids: [],
			count: 0,
			documentType: DOCUMENT_TYPE.Certificate,
			readable () { return `${'certificat'.pluralize(this.count, false)}`; },
		},
		{
			type: 'Bill',
			api: this.self.isClient() ? getApi(this.self.userType, DOCUMENT_TYPE.Bill) : undefined,
			ids: [],
			count: 0,
			documentType: DOCUMENT_TYPE.Bill,
			readable () { return `${'facture'.pluralize(this.count, false)}`; },
		},
		{
			type: 'TempWorkerContract',
			api: getApi(this.self.userType, DOCUMENT_TYPE.TempWorkerContract),
			ids: [],
			count: 0,
			service: useContractService(DOCUMENT_TYPE.TempWorkerContract),
			documentType: DOCUMENT_TYPE.TempWorkerContract,
			readable (isAgency: boolean) { return `${'contrat'.pluralize(this.count, false)} ${isAgency ? 'de mission' : ''}`; },
		},
		{
			type: 'ClientContract',
			api: getApi(this.self.userType, DOCUMENT_TYPE.ClientContract),
			ids: [],
			count: 0,
			service: useContractService(DOCUMENT_TYPE.ClientContract),
			documentType: DOCUMENT_TYPE.ClientContract,
			readable (isAgency: boolean) { return `${'contrat'.pluralize(this.count, false)} ${isAgency ? 'de mise à disposition' : ''}`; },
		},
		{
			type: 'SalarySlip',
			api: getApi(this.self.userType, DOCUMENT_TYPE.SalarySlip),
			ids: [],
			count: 0,
			documentType: DOCUMENT_TYPE.SalarySlip,
			readable () { return `${'bulletin'.pluralize(this.count, false)} de salaire`; },
		},
		{
			type: 'TempWorkerProvisionDocument',
			api: getApi(this.self.userType, DOCUMENT_TYPE.TempWorkerProvisionDocument),
			ids: [],
			count: 0,
			service: useProvisionDocumentService(DOCUMENT_TYPE.TempWorkerProvisionDocument),
			documentType: DOCUMENT_TYPE.TempWorkerProvisionDocument,
			readable () { return `${'document'.pluralize(this.count, false)} mis à disposition`; },
		},
		{
			type: 'ClientProvisionDocument',
			api: this.self.isClient() ? getApi(this.self.userType, DOCUMENT_TYPE.ClientProvisionDocument) : undefined,
			ids: [],
			count: 0,
			service: useProvisionDocumentService(DOCUMENT_TYPE.ClientProvisionDocument),
			documentType: DOCUMENT_TYPE.ClientProvisionDocument,
			readable () { return `${'document'.pluralize(this.count, false)} mis à disposition`; },
		},
	] as DocumentType[];

	get apiMethod () {
		return this.props.type === 'notViewed'
			? 'getNotViewed'
			: this.props.late ? 'getNotSignedLate' : 'getNotSigned';
	}

	get newItems () { return this.newDocs.filter(x => x.count);}
	get dataLoaded () { return this.state.dataLoaded;}
	get notViewedDocuments () { return this.newDocs;}


	constructor (props?: Props) {
		super(props);
	}

	protected onMounted () {
		super.onMounted();
		if (this.props.type === 'notSigned') {
			this.registerBusEvent('reload', () => {
				this.state.dataLoaded = false;
				setTimeout(() => {
					this.onBeforeMountAsync();
				}, 3000);
			});
		}
	}

	protected async onBeforeMountAsync () {
		for (const docType of this.newDocs) {
			await this.getDataAsync(docType);
		}
		this.state.dataLoaded = true;
	}


	canBeViewedByUser (docType: DOCUMENT_TYPE) {
		if (this.self.isTempWorker()) return true;
		switch (docType) {
		case DOCUMENT_TYPE.Bill:
			return this.self.can('BillRead');
		case DOCUMENT_TYPE.TempWorkerContract:
			return this.self.can('TempWorkerContractRead');
		case DOCUMENT_TYPE.ClientContract:
			return this.self.can('ClientContractRead');
		case DOCUMENT_TYPE.SalarySlip:
			return this.self.can('SalarySlipRead');
		case DOCUMENT_TYPE.TempWorkerProvisionDocument:
			return this.self.can('TempWorkerProvisionDocumentRead');
		case DOCUMENT_TYPE.ClientProvisionDocument:
			return this.self.can('ClientProvisionDocumentRead');
		default:
			return false;
		}
	}

	canBeSignedByUser (docType: DOCUMENT_TYPE) {
		switch (docType) {
		case DOCUMENT_TYPE.ClientContract:
			return this.self.can('ClientContractSign');
		case DOCUMENT_TYPE.ClientProvisionDocument:
			return this.self.can('ClientProvisionDocumentSign');
		default:
			return false;
		}
	}

	async getDataAsync (docType: DocumentType) {
		const { api } = docType;
		if (api && itemHas(api, this.apiMethod)) {
			const res = await api[this.apiMethod]();
			if (isArray(res?.data)) {
				docType.ids = res.data;
				docType.count = res.data.length;
			} else if (isFinite(res?.data)) {
				docType.count = res.data;
			}
		}
	}

	handleClick (doc: DocumentType) {
		if (this.props.type === 'notViewed' || this.self.isAgency()) this.view(doc);

		else if (this.self.isClient() && doc.count > 40) this.view(doc);

		else if (this.self.isTempWorker() && doc.count > 20) this.view(doc);

		else if ((this.self.isAgency() || this.self.isClient()) && !this.canBeSignedByUser(doc.documentType)) this.view(doc);

		else this.signatureAsync(doc);
	}

	docButtonLabel (doc: DocumentType) {
		if (this.props.type === 'notViewed' || this.self.isAgency()) return 'Voir';

		if (this.self.isClient() && doc.count > 40) return 'Voir';

		if (this.self.isTempWorker() && doc.count > 20) return 'Voir';

		if ((this.self.isAgency() || this.self.isClient()) && !this.canBeSignedByUser(doc.documentType)) return `Voir`;

		return 'Signer';
	}

	async signatureAsync (doc: DocumentType) {
		if (doc.service !== undefined) {
			const success = await doc.service.signAsync(doc.ids.map(x => ({ id: x })), false, doc.documentType);
			if (success) {
				doc.ids = [];
				doc.count = 0;
			}
		}
	}

	view (doc: DocumentType) {
		const filter : FilterType = {};
		if (this.props.type === 'notViewed') {
			filter.documentState = DOCUMENT_STATE.NotView;
		} else {
			const documentStatus = (this.self.isAgency() && this.props.late) ? DOCUMENT_STATUS.NotSignedLate : DOCUMENT_STATUS.NotSigned;
			filter.documentStatus = documentStatus;
		}

		this.router.push(
			{
				name: doc.type,
				query: { filter: qsTo64(filter) },
			},
		);
	}
}
